import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import config from '../config/config';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import { motion } from 'framer-motion';

const Login: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat/new');
    }
  }, [isAuthenticated, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      position="relative"
      padding={2}
      sx={{
        overflow: 'hidden',
        backgroundImage: 'url(/bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Background Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          zIndex: 1,
        }}
      />

      {/* Animated Avatar bottom right */}
      <motion.div
        initial={{ scale: 1, rotate: 0 }}
        animate={{
          scale: [1, 1.05, 1],
          rotate: [0, -2, 2, -2, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          repeatDelay: 2,
          ease: 'easeInOut',
        }}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <img
          src="/avatar_login.png"
          alt="Avatar"
          style={{
            width: 'clamp(100px, 15vw, 130px)',
            opacity: 0.9,
            filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.3))',
          }}
        />
      </motion.div>

      <Box
        position="relative"
        zIndex={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        maxWidth={420}
      >
        {/* Login Container */}
        <Box
          sx={{
            width: '100%',
            pt: 5,
            pb: { xs: 3, sm: 4 },
            px: { xs: 3, sm: 4 },
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(40px) saturate(250%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50px',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.25)',
          }}
        >
          {/* Large Centered Logo */}
          <Box display="flex" justifyContent="center" mb={2}>
            <img
              src="/tat_logo.png"
              alt="TAT Logo"
              style={{
                width: 'clamp(120px, 20vw, 200px)',
                height: 'auto',
              }}
            />
          </Box>

          <Typography
            variant="h4"
            color="white"
            fontWeight="700"
            sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}
          >
            Copilot
          </Typography>
          <Typography
            variant="body1"
            color="rgba(255, 255, 255, 0.9)"
            sx={{ mt: 0.5, mb: 3 }}
          >
            สำหรับเจ้าหน้าที่ ททท.
          </Typography>

          {isLoading ? (
            <CircularProgress color="inherit" sx={{ color: 'white' }} />
          ) : (
            <Button
              size="large"
              startIcon={<KeyOutlinedIcon />}
              onClick={() => {
                window.open(config.signInUrl, '_blank', 'noopener,noreferrer');
              }}
              fullWidth
              sx={{
                py: '14px',
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: '600',
                color: 'white',
                background: 'rgba(255, 255, 255, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '25px',
                transition:
                  'background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  transform: 'scale(1.02)',
                },
              }}
            >
              เข้าสู่ระบบด้วยอีเมลองค์กร
            </Button>
          )}

          {error && (
            <Typography color="error.light" sx={{ mt: 2, fontSize: '0.9rem' }}>
              {error}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
